import React from 'react';
import { useNavigate } from 'react-router';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { Points } from 'components/Points/Points';
import RankTierBadge from '../RankTierBadge';
import { LeaderboardEntry, LeaderboardCategory, CATEGORY_CONFIG } from 'types/interfaces/leaderboard';
import './LeaderboardPodium.css';

interface LeaderboardPodiumProps {
  podium: LeaderboardEntry[];
  category: LeaderboardCategory;
  isLoading?: boolean;
}

const LeaderboardPodium: React.FC<LeaderboardPodiumProps> = ({
  podium,
  category,
  isLoading,
}) => {
  const navigate = useNavigate();
  const statLabel = CATEGORY_CONFIG[category].statLabel;

  const handleClick = (username: string) => {
    navigate(`/profile/u/${encodeURIComponent(username)}`);
  };

  const formatDisplayValue = (value: number | string, cat: LeaderboardCategory): string => {
    if (typeof value === 'string') return value;

    if (cat === 'highest_rated') {
      return value.toFixed(1);
    }
    if (cat === 'rising_stars') {
      return `+${value}%`;
    }
    return value.toLocaleString();
  };

  const formatRelativeDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return '1 week ago';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 60) return '1 month ago';
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (isLoading) {
    return (
      <div className="leaderboard-podium leaderboard-podium--loading">
        <div className="leaderboard-podium__skeleton leaderboard-podium__skeleton--second" />
        <div className="leaderboard-podium__skeleton leaderboard-podium__skeleton--first" />
        <div className="leaderboard-podium__skeleton leaderboard-podium__skeleton--third" />
      </div>
    );
  }

  if (podium.length === 0) {
    return null;
  }

  // Reorder for display: [#2, #1, #3] on desktop
  const first = podium.find((e) => e.rank === 1);
  const second = podium.find((e) => e.rank === 2);
  const third = podium.find((e) => e.rank === 3);

  const renderPodiumCard = (
    entry: LeaderboardEntry | undefined,
    position: 'first' | 'second' | 'third'
  ) => {
    if (!entry) return null;

    const positionLabels = {
      first: '1st',
      second: '2nd',
      third: '3rd',
    };

    return (
      <div
        className={`leaderboard-podium__card leaderboard-podium__card--${position}`}
        onClick={() => handleClick(entry.username)}
      >
        <div className={`leaderboard-podium__rank-badge leaderboard-podium__rank-badge--${position}`}>
          {positionLabels[position]}
        </div>
        <AvatarWithFlag
          avatarUrl={entry.avatarUrl}
          username={entry.username}
          size={position === 'first' ? 80 : 64}
          countryCode={entry.country}
        />
        <div className="leaderboard-podium__username">{entry.username}</div>
        <RankTierBadge tier={entry.tier} size={position === 'first' ? 'medium' : 'small'} />
        <div className="leaderboard-podium__stat">
          {category === 'new_members' ? (
            <div className="leaderboard-podium__new-member-stats">
              <span className="leaderboard-podium__stat-value">
                {formatRelativeDate(entry.memberSince)}
              </span>
              <Points points={entry.points} type="small" />
            </div>
          ) : category === 'most_points' ? (
            <Points points={typeof entry.displayValue === 'number' ? entry.displayValue : entry.points} type="default" />
          ) : (
            <>
              <span className="leaderboard-podium__stat-value">
                {formatDisplayValue(entry.displayValue, category)}
              </span>
              <span className="leaderboard-podium__stat-label">{statLabel}</span>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="leaderboard-podium">
      {renderPodiumCard(second, 'second')}
      {renderPodiumCard(first, 'first')}
      {renderPodiumCard(third, 'third')}
    </div>
  );
};

export default LeaderboardPodium;
